import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getContentByCategory, getImageUrl } from '../services/api';
import { articleUrl } from '../services/slugify';
import SiteHeader from '../components/SiteHeader';
import SiteFooter from '../components/SiteFooter';

// ── Shared helpers ────────────────────────────────────────────────────────────

const META = {
  longevity: {
    title: 'Longevity',
    tagline: 'The Science of Aging',
    description: 'Cutting-edge research and insights on extending healthspan and living better for longer.',
    accent: '#1b5e20',
    accentLight: '#e8f5e9',
    accentMid: '#388e3c',
    hero: '#0d3b1a',
  },
  nutrition: {
    title: 'Nutrition',
    tagline: 'Diet & Metabolic Health',
    description: 'Evidence-based guidance on food, metabolism, and eating patterns that fuel a longer, healthier life.',
    accent: '#1565c0',
    accentLight: '#e3f2fd',
    accentMid: '#1976d2',
    hero: '#0a2a5e',
  },
  research: {
    title: 'Research',
    tagline: 'Clinical Trials & Discoveries',
    description: 'Peer-reviewed findings, trial results, and breakthrough science straight from the lab.',
    accent: '#4a148c',
    accentLight: '#f3e5f5',
    accentMid: '#7b1fa2',
    hero: '#1a0030',
  },
  'mental-health': {
    title: 'Mental Health',
    tagline: 'Psychology & Neurological Wellness',
    description: 'Exploring the mind-body connection, mental resilience, and the neuroscience of wellbeing.',
    accent: '#4e342e',
    accentLight: '#fbe9e7',
    accentMid: '#795548',
    hero: '#2c1a00',
  },
  prevention: {
    title: 'Prevention',
    tagline: 'Actionable Daily Habits',
    description: 'Practical evidence-based habits, routines, and strategies to prevent disease and optimise daily wellness.',
    accent: '#bf360c',
    accentLight: '#fbe9e7',
    accentMid: '#e64a19',
    hero: '#5d1a00',
  },
};

function extractFirstImage(htmlContent, savedImageUrl) {
  if (savedImageUrl) return getImageUrl(savedImageUrl);
  if (!htmlContent) return null;
  const imgMatch = htmlContent.match(/<img[^>]+src=["']([^"']+)["']/i);
  if (imgMatch && imgMatch[1]) return imgMatch[1];
  return null;
}

function formatDate(dateString) {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function extractExcerpt(htmlContent, maxLength = 140) {
  if (!htmlContent) return '';
  let cleaned = htmlContent
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '')
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, '');
  const text = cleaned.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  return text.length > maxLength ? text.substring(0, maxLength) + '…' : text;
}

// ════════════════════════════════════════════════════════════════
// LAYOUT 1: LONGEVITY — Deep Forest Green, Alternating Story Rows
// ════════════════════════════════════════════════════════════════
function LongevityLayout({ articles, navigate }) {
  const meta = META.longevity;
  const hero = articles[0];
  const rest = articles.slice(1);

  return (
    <div style={{ background: '#f9fafb' }}>
      {/* Category Banner */}
      <div style={{ background: meta.hero, padding: '48px 0 36px', borderBottom: `4px solid ${meta.accentMid}` }}>
        <div className="container">
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '12px' }}>
            <div style={{ width: '5px', height: '48px', background: '#c9a84c', borderRadius: '3px', flexShrink: 0 }} />
            <div>
              <div style={{ color: '#c9a84c', fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '3px', fontWeight: 700, marginBottom: '4px' }}>
                Daily Health · {meta.tagline}
              </div>
              <h1 style={{ color: '#fff', fontSize: '2.8em', fontWeight: 800, margin: 0, lineHeight: 1.1 }}>{meta.title}</h1>
            </div>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '1.05em', maxWidth: '600px', marginLeft: '21px' }}>{meta.description}</p>
        </div>
      </div>

      <div className="container" style={{ padding: '40px 20px' }}>
        {articles.length === 0 && (
          <EmptyState meta={meta} />
        )}

        {/* Hero */}
        {hero && (
          <article onClick={() => navigate(`/${articleUrl(hero.title, hero.id, articles)}`)} style={{
            display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0',
            background: '#fff', borderRadius: '12px', overflow: 'hidden',
            boxShadow: '0 4px 24px rgba(0,0,0,0.1)', marginBottom: '40px', cursor: 'pointer',
            border: `2px solid ${meta.accentMid}`,
          }}>
            <div style={{ position: 'relative', overflow: 'hidden', minHeight: '340px' }}>
              {extractFirstImage(hero.htmlContent, hero.imageUrl) ? (
                <img src={extractFirstImage(hero.htmlContent, hero.imageUrl)} alt={hero.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', background: meta.hero, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <span style={{ fontSize: '4em' }}>🧬</span>
                </div>
              )}
              <div style={{ position: 'absolute', top: '16px', left: '16px', background: '#c9a84c', color: '#0d3b1a', padding: '4px 12px', borderRadius: '4px', fontSize: '0.72em', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1px' }}>
                Featured
              </div>
            </div>
            <div style={{ padding: '36px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ color: meta.accentMid, fontSize: '0.78em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1.5px', marginBottom: '12px' }}>
                {meta.tagline}
              </div>
              <h2 style={{ fontSize: '1.65em', fontWeight: 800, color: '#1a1a1a', lineHeight: 1.25, marginBottom: '14px' }}>{hero.title}</h2>
              <p style={{ color: '#555', lineHeight: 1.7, marginBottom: '20px', fontSize: '0.95em' }}>{extractExcerpt(hero.htmlContent, 180)}</p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ fontSize: '0.8em', color: '#999' }}>{formatDate(hero.createdAt)}</span>
                <span style={{ color: meta.accentMid, fontSize: '0.88em', fontWeight: 600 }}>Read more →</span>
              </div>
            </div>
          </article>
        )}

        {/* Alternating rows */}
        {rest.map((article, idx) => {
          const img = extractFirstImage(article.htmlContent, article.imageUrl);
          const isEven = idx % 2 === 0;
          return (
            <article key={article.id} onClick={() => navigate(`/${articleUrl(article.title, article.id, articles)}`)}
              style={{
                display: 'grid', gridTemplateColumns: img ? '300px 1fr' : '1fr',
                flexDirection: isEven ? 'row' : 'row-reverse',
                gap: '0', background: '#fff', borderRadius: '10px', overflow: 'hidden',
                boxShadow: '0 2px 12px rgba(0,0,0,0.07)', marginBottom: '20px', cursor: 'pointer',
                borderLeft: `4px solid ${idx === 0 ? '#c9a84c' : meta.accentMid}`,
                direction: isEven ? 'ltr' : 'rtl',
              }}>
              {img && (
                <div style={{ overflow: 'hidden', direction: 'ltr' }}>
                  <img src={img} alt={article.title}
                    style={{ width: '300px', height: '200px', objectFit: 'cover', display: 'block' }} />
                </div>
              )}
              <div style={{ padding: '22px 28px', direction: 'ltr' }}>
                <h3 style={{ fontSize: '1.15em', fontWeight: 700, color: '#1a1a1a', marginBottom: '10px', lineHeight: 1.3 }}>{article.title}</h3>
                <p style={{ color: '#666', fontSize: '0.88em', lineHeight: 1.6, marginBottom: '12px' }}>{extractExcerpt(article.htmlContent, 120)}</p>
                <span style={{ fontSize: '0.78em', color: '#999' }}>{formatDate(article.createdAt)}</span>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// LAYOUT 2: NUTRITION — Royal Blue, Magazine Grid
// ════════════════════════════════════════════════════════════════
function NutritionLayout({ articles, navigate }) {
  const meta = META.nutrition;
  const featured = articles.slice(0, 4);
  const grid = articles.slice(4);

  return (
    <div style={{ background: '#f4f8ff' }}>
      {/* Banner */}
      <div style={{ background: `linear-gradient(135deg, ${meta.hero} 0%, #0d47a1 100%)`, padding: '48px 0 40px' }}>
        <div className="container">
          <span style={{ display: 'inline-block', background: meta.accentMid, color: '#fff', padding: '4px 14px', borderRadius: '20px', fontSize: '0.72em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '14px' }}>
            {meta.tagline}
          </span>
          <h1 style={{ color: '#fff', fontSize: '3em', fontWeight: 800, margin: '0 0 10px', lineHeight: 1 }}>{meta.title}</h1>
          <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: '1em', maxWidth: '560px' }}>{meta.description}</p>
        </div>
      </div>

      <div className="container" style={{ padding: '40px 20px' }}>
        {articles.length === 0 && <EmptyState meta={meta} />}

        {/* Magazine top section: 1 big + 3 stacked */}
        {featured.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: '3fr 2fr', gap: '20px', marginBottom: '40px' }}>
            {/* Big featured left */}
            {featured[0] && (
              <article onClick={() => navigate(`/${articleUrl(featured[0].title, featured[0].id, articles)}`)} style={{
                background: '#fff', borderRadius: '12px', overflow: 'hidden',
                boxShadow: '0 4px 20px rgba(0,0,0,0.1)', cursor: 'pointer',
              }}>
                {extractFirstImage(featured[0].htmlContent, featured[0].imageUrl) ? (
                  <img src={extractFirstImage(featured[0].htmlContent, featured[0].imageUrl)} alt={featured[0].title}
                    style={{ width: '100%', height: '280px', objectFit: 'cover', display: 'block' }} />
                ) : (
                  <div style={{ height: '280px', background: `linear-gradient(135deg, ${meta.hero}, ${meta.accentMid})`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '4em' }}>🥗</div>
                )}
                <div style={{ padding: '24px' }}>
                  <span style={{ background: meta.accentLight, color: meta.accent, padding: '3px 10px', borderRadius: '4px', fontSize: '0.72em', fontWeight: 700, textTransform: 'uppercase' }}>Top Story</span>
                  <h2 style={{ fontSize: '1.4em', fontWeight: 800, color: '#1a1a1a', margin: '12px 0 10px', lineHeight: 1.25 }}>{featured[0].title}</h2>
                  <p style={{ color: '#666', fontSize: '0.9em', lineHeight: 1.6 }}>{extractExcerpt(featured[0].htmlContent, 160)}</p>
                  <span style={{ display: 'block', marginTop: '12px', fontSize: '0.8em', color: '#999' }}>{formatDate(featured[0].createdAt)}</span>
                </div>
              </article>
            )}

            {/* 3 stacked right */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {featured.slice(1, 4).map((article) => {
                const img = extractFirstImage(article.htmlContent, article.imageUrl);
                return (
                  <article key={article.id} onClick={() => navigate(`/${articleUrl(article.title, article.id, articles)}`)} style={{
                    background: '#fff', borderRadius: '10px', overflow: 'hidden',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.08)', cursor: 'pointer',
                    display: 'flex', gap: '0', flex: 1,
                  }}>
                    {img && (
                      <img src={img} alt={article.title}
                        style={{ width: '110px', height: '100%', objectFit: 'cover', flexShrink: 0, maxHeight: '110px' }} />
                    )}
                    <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <h3 style={{ fontSize: '0.95em', fontWeight: 700, color: '#1a1a1a', marginBottom: '6px', lineHeight: 1.3 }}>{article.title}</h3>
                      <span style={{ fontSize: '0.75em', color: '#999' }}>{formatDate(article.createdAt)}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        )}

        {/* Bottom 4-col grid */}
        {grid.length > 0 && (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{ height: '3px', background: meta.accentMid, width: '40px', borderRadius: '3px' }} />
              <h2 style={{ fontSize: '1em', fontWeight: 700, color: meta.accent, textTransform: 'uppercase', letterSpacing: '1.5px' }}>More Articles</h2>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '20px' }}>
              {grid.map((article) => {
                const img = extractFirstImage(article.htmlContent, article.imageUrl);
                return (
                  <article key={article.id} onClick={() => navigate(`/${articleUrl(article.title, article.id, articles)}`)} style={{
                    background: '#fff', borderRadius: '10px', overflow: 'hidden',
                    boxShadow: '0 2px 10px rgba(0,0,0,0.07)', cursor: 'pointer',
                  }}>
                    {img ? (
                      <img src={img} alt={article.title} style={{ width: '100%', height: '170px', objectFit: 'cover', display: 'block' }} />
                    ) : (
                      <div style={{ height: '170px', background: meta.accentLight, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5em' }}>🥗</div>
                    )}
                    <div style={{ padding: '18px' }}>
                      <h3 style={{ fontSize: '1em', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px', lineHeight: 1.3 }}>{article.title}</h3>
                      <p style={{ color: '#777', fontSize: '0.83em', lineHeight: 1.5, marginBottom: '10px' }}>{extractExcerpt(article.htmlContent, 90)}</p>
                      <span style={{ fontSize: '0.75em', color: '#aaa' }}>{formatDate(article.createdAt)}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// LAYOUT 3: RESEARCH — Deep Purple, Academic/Journal Style
// ════════════════════════════════════════════════════════════════
function ResearchLayout({ articles, navigate }) {
  const meta = META.research;

  return (
    <div style={{ background: '#f9f7ff' }}>
      {/* Banner */}
      <div style={{ background: meta.hero, padding: '48px 0 36px', borderBottom: `3px solid ${meta.accentMid}` }}>
        <div className="container" style={{ display: 'flex', alignItems: 'flex-end', gap: '32px' }}>
          <div style={{ flex: 1 }}>
            <div style={{ color: meta.accentMid, fontSize: '0.75em', textTransform: 'uppercase', letterSpacing: '3px', fontWeight: 700, marginBottom: '10px' }}>
              Daily Health · {meta.tagline}
            </div>
            <h1 style={{ color: '#fff', fontSize: '2.8em', fontWeight: 900, margin: 0, lineHeight: 1 }}>{meta.title}</h1>
          </div>
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.95em', maxWidth: '420px', lineHeight: 1.6, paddingBottom: '4px' }}>{meta.description}</p>
        </div>
      </div>

      <div className="container" style={{ padding: '40px 20px' }}>
        {articles.length === 0 && <EmptyState meta={meta} />}

        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '32px', alignItems: 'start' }}>
          {/* Main column — numbered journal-style */}
          <div>
            {articles.map((article, idx) => {
              const img = extractFirstImage(article.htmlContent, article.imageUrl);
              return (
                <article key={article.id} onClick={() => navigate(`/${articleUrl(article.title, article.id, articles)}`)}
                  style={{
                    display: 'flex', gap: '0', background: '#fff', borderRadius: '8px',
                    overflow: 'hidden', marginBottom: '16px', cursor: 'pointer',
                    boxShadow: '0 2px 8px rgba(74,20,140,0.08)',
                    border: '1px solid #e8e0f5',
                    borderLeft: `5px solid ${idx === 0 ? meta.accentMid : idx < 3 ? '#9c27b0' : '#ce93d8'}`,
                  }}>
                  {/* Article number */}
                  <div style={{
                    width: '52px', flexShrink: 0, background: idx === 0 ? meta.accentMid : meta.accentLight,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1.3em', fontWeight: 900, color: idx === 0 ? '#fff' : meta.accent,
                  }}>
                    {String(idx + 1).padStart(2, '0')}
                  </div>
                  {img && (
                    <img src={img} alt={article.title}
                      style={{ width: '140px', height: '120px', objectFit: 'cover', flexShrink: 0 }} />
                  )}
                  <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    {idx === 0 && (
                      <span style={{ display: 'inline-block', background: meta.accentMid, color: '#fff', padding: '2px 10px', borderRadius: '3px', fontSize: '0.68em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
                        Latest Finding
                      </span>
                    )}
                    <h3 style={{ fontSize: idx === 0 ? '1.15em' : '1em', fontWeight: 700, color: '#1a1a1a', marginBottom: '8px', lineHeight: 1.35 }}>{article.title}</h3>
                    <p style={{ color: '#666', fontSize: '0.85em', lineHeight: 1.55, marginBottom: '10px' }}>{extractExcerpt(article.htmlContent, idx === 0 ? 160 : 100)}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ fontSize: '0.75em', color: '#aaa' }}>{formatDate(article.createdAt)}</span>
                      <span style={{ fontSize: '0.78em', color: meta.accentMid, fontWeight: 600 }}>Read paper →</span>
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {/* Sidebar: index cards */}
          <aside>
            <div style={{ background: meta.accentLight, borderRadius: '10px', padding: '20px', border: `1px solid #e1d5f5`, position: 'sticky', top: '120px' }}>
              <h3 style={{ fontSize: '0.85em', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '1.5px', color: meta.accent, marginBottom: '16px', paddingBottom: '10px', borderBottom: `2px solid ${meta.accentMid}` }}>
                All Studies
              </h3>
              {articles.map((article, idx) => (
                <div key={article.id} onClick={() => navigate(`/${articleUrl(article.title, article.id, articles)}`)}
                  style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #e8e0f5', cursor: 'pointer' }}>
                  <span style={{ fontSize: '0.75em', fontWeight: 800, color: meta.accentMid, minWidth: '22px', marginTop: '1px' }}>{idx + 1}.</span>
                  <span style={{ fontSize: '0.85em', color: '#333', lineHeight: 1.4, fontWeight: 500 }}>{article.title}</span>
                </div>
              ))}
              {articles.length === 0 && (
                <p style={{ fontSize: '0.85em', color: '#999' }}>No articles yet.</p>
              )}
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// LAYOUT 4: MENTAL HEALTH — Warm Earth, Soft Card Grid
// ════════════════════════════════════════════════════════════════
function MentalHealthLayout({ articles, navigate }) {
  const meta = META['mental-health'];
  const hero = articles[0];
  const rest = articles.slice(1);

  return (
    <div style={{ background: '#fdf8f5' }}>
      {/* Banner — warm, gentle */}
      <div style={{ background: `linear-gradient(160deg, ${meta.hero} 0%, #5d3a1a 100%)`, padding: '52px 0 40px' }}>
        <div className="container">
          <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.78em', textTransform: 'uppercase', letterSpacing: '3px', fontWeight: 600, marginBottom: '10px' }}>
            {meta.tagline}
          </p>
          <h1 style={{ color: '#fff', fontSize: '2.8em', fontWeight: 800, margin: '0 0 12px', lineHeight: 1.1 }}>{meta.title}</h1>
          <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '520px', lineHeight: 1.7 }}>{meta.description}</p>
        </div>
      </div>

      <div className="container" style={{ padding: '40px 20px' }}>
        {articles.length === 0 && <EmptyState meta={meta} />}

        {/* Large hero card with quote-style */}
        {hero && (
          <article onClick={() => navigate(`/${articleUrl(hero.title, hero.id, articles)}`)} style={{
            background: '#fff', borderRadius: '16px', overflow: 'hidden',
            boxShadow: '0 6px 30px rgba(78,52,46,0.12)', marginBottom: '36px', cursor: 'pointer',
            display: 'grid', gridTemplateColumns: extractFirstImage(hero.htmlContent, hero.imageUrl) ? '1fr 1fr' : '1fr',
          }}>
            {extractFirstImage(hero.htmlContent, hero.imageUrl) && (
              <div style={{ position: 'relative', overflow: 'hidden', minHeight: '300px' }}>
                <img src={extractFirstImage(hero.htmlContent, hero.imageUrl)} alt={hero.title}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: 'brightness(0.92)' }} />
              </div>
            )}
            <div style={{ padding: '40px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
              <div style={{ width: '40px', height: '4px', background: meta.accentMid, borderRadius: '4px', marginBottom: '20px' }} />
              <h2 style={{ fontSize: '1.55em', fontWeight: 800, color: '#2c1a00', lineHeight: 1.3, marginBottom: '16px' }}>{hero.title}</h2>
              <p style={{ color: '#777', lineHeight: 1.8, fontSize: '0.95em', marginBottom: '24px', fontStyle: 'italic', borderLeft: `3px solid ${meta.accentMid}`, paddingLeft: '16px' }}>
                {extractExcerpt(hero.htmlContent, 180)}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <span style={{ fontSize: '0.8em', color: '#bbb' }}>{formatDate(hero.createdAt)}</span>
                <span style={{ color: meta.accentMid, fontSize: '0.9em', fontWeight: 600 }}>Continue reading →</span>
              </div>
            </div>
          </article>
        )}

        {/* Soft card grid */}
        {rest.length > 0 && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '22px' }}>
            {rest.map((article, idx) => {
              const img = extractFirstImage(article.htmlContent, article.imageUrl);
              const bgColors = ['#fff9f5', '#f5f0ff', '#fff5f0', '#f0f8f5'];
              const borderColors = ['#e8d5c0', '#ddd0f0', '#f0ccc0', '#c0e8d5'];
              return (
                <article key={article.id} onClick={() => navigate(`/${articleUrl(article.title, article.id, articles)}`)} style={{
                  background: bgColors[idx % 4], borderRadius: '14px', overflow: 'hidden',
                  border: `1px solid ${borderColors[idx % 4]}`,
                  boxShadow: '0 3px 14px rgba(78,52,46,0.07)', cursor: 'pointer',
                  transition: 'transform 0.2s',
                }}>
                  {img ? (
                    <img src={img} alt={article.title} style={{ width: '100%', height: '180px', objectFit: 'cover', display: 'block' }} />
                  ) : (
                    <div style={{ height: '100px', background: borderColors[idx % 4], display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '2.5em' }}>🧠</div>
                  )}
                  <div style={{ padding: '20px' }}>
                    <h3 style={{ fontSize: '1em', fontWeight: 700, color: '#2c1a00', marginBottom: '10px', lineHeight: 1.4 }}>{article.title}</h3>
                    <p style={{ color: '#887a72', fontSize: '0.85em', lineHeight: 1.6, marginBottom: '12px' }}>{extractExcerpt(article.htmlContent, 110)}</p>
                    <span style={{ fontSize: '0.75em', color: '#bbb' }}>{formatDate(article.createdAt)}</span>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// LAYOUT 5: PREVENTION — Energetic Orange, Wide Action Cards
// ════════════════════════════════════════════════════════════════
function PreventionLayout({ articles, navigate }) {
  const meta = META.prevention;

  return (
    <div style={{ background: '#fffaf7' }}>
      {/* Banner — bold, energetic */}
      <div style={{ background: meta.hero, padding: '0' }}>
        <div style={{ background: `linear-gradient(90deg, ${meta.hero} 50%, ${meta.accentMid} 100%)`, padding: '48px 0 36px' }}>
          <div className="container">
            <span style={{ display: 'inline-block', background: '#fff', color: meta.accentMid, padding: '5px 14px', borderRadius: '20px', fontSize: '0.72em', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '14px' }}>
              Daily Health · {meta.tagline}
            </span>
            <h1 style={{ color: '#fff', fontSize: '2.8em', fontWeight: 900, margin: '0 0 10px', lineHeight: 1 }}>{meta.title}</h1>
            <p style={{ color: 'rgba(255,255,255,0.8)', maxWidth: '540px', fontSize: '1em', lineHeight: 1.6 }}>{meta.description}</p>
          </div>
        </div>
      </div>

      <div className="container" style={{ padding: '40px 20px' }}>
        {articles.length === 0 && <EmptyState meta={meta} />}

        {/* Bold numbered wide cards */}
        {articles.map((article, idx) => {
          const img = extractFirstImage(article.htmlContent, article.imageUrl);
          const isHero = idx === 0;
          return (
            <article key={article.id} onClick={() => navigate(`/${articleUrl(article.title, article.id, articles)}`)}
              style={{
                display: 'grid',
                gridTemplateColumns: img ? (isHero ? '1fr 2fr' : '120px 1fr') : '72px 1fr',
                background: '#fff', borderRadius: '12px', overflow: 'hidden',
                marginBottom: '16px', cursor: 'pointer',
                boxShadow: isHero ? '0 6px 24px rgba(191,54,12,0.15)' : '0 2px 10px rgba(0,0,0,0.07)',
                border: isHero ? `2px solid ${meta.accentMid}` : '1px solid #f0ddd5',
              }}>
              {/* Number badge */}
              {!img && (
                <div style={{
                  background: idx === 0 ? meta.accentMid : idx < 5 ? meta.accent : '#e0cfc0',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: isHero ? '2.5em' : '1.8em', fontWeight: 900,
                  color: '#fff',
                }}>
                  {idx + 1}
                </div>
              )}
              {img && (
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  <img src={img} alt={article.title}
                    style={{ width: '100%', height: isHero ? '100%' : '130px', objectFit: 'cover', display: 'block', minHeight: isHero ? '240px' : 'auto' }} />
                  <div style={{
                    position: 'absolute', top: '12px', left: '12px',
                    background: meta.accentMid, color: '#fff',
                    width: '36px', height: '36px', borderRadius: '50%',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '1em', fontWeight: 900, boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
                  }}>
                    {idx + 1}
                  </div>
                </div>
              )}
              <div style={{ padding: isHero ? '30px 32px' : '18px 22px', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                {isHero && (
                  <span style={{ display: 'inline-block', background: meta.accentLight, color: meta.accent, padding: '3px 12px', borderRadius: '4px', fontSize: '0.72em', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', alignSelf: 'flex-start' }}>
                    Top Tip
                  </span>
                )}
                <h3 style={{ fontSize: isHero ? '1.35em' : '1em', fontWeight: 800, color: '#1a1a1a', marginBottom: isHero ? '12px' : '8px', lineHeight: 1.3 }}>{article.title}</h3>
                <p style={{ color: '#666', fontSize: isHero ? '0.95em' : '0.85em', lineHeight: 1.65, marginBottom: '12px' }}>
                  {extractExcerpt(article.htmlContent, isHero ? 180 : 100)}
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <span style={{ fontSize: '0.75em', color: '#bbb' }}>{formatDate(article.createdAt)}</span>
                  <span style={{ fontSize: '0.82em', color: meta.accentMid, fontWeight: 700 }}>Take action →</span>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}

// ── Empty state ───────────────────────────────────────────────────────────────
function EmptyState({ meta }) {
  return (
    <div style={{ textAlign: 'center', padding: '80px 20px', color: '#aaa' }}>
      <div style={{ fontSize: '3.5em', marginBottom: '16px' }}>📭</div>
      <h3 style={{ fontSize: '1.2em', fontWeight: 600, color: '#888', marginBottom: '8px' }}>No articles yet</h3>
      <p style={{ fontSize: '0.9em' }}>Check back soon — new content is on the way.</p>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════
// MAIN CategoryPage
// ════════════════════════════════════════════════════════════════
export default function CategoryPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  const meta = META[slug];

  useEffect(() => {
    setLoading(true);
    getContentByCategory(slug)
      .then((res) => {
        if (res.success) setArticles(res.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (!meta) {
    return (
      <div className="news-portal">
        <SiteHeader activeSlug={slug} />
        <div style={{ padding: '80px 20px', textAlign: 'center' }}>
          <h2>Category not found</h2>
          <a href="/">← Back to Home</a>
        </div>
        <SiteFooter />
      </div>
    );
  }

  if (loading) {
    return (
      <div className="news-portal">
        <SiteHeader activeSlug={slug} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, padding: '80px 20px', flexDirection: 'column', gap: '16px' }}>
          <div className="spinner" />
          <p style={{ color: '#888' }}>Loading {meta.title} articles…</p>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const layoutProps = { articles, navigate };

  return (
    <div className="news-portal">
      <SiteHeader activeSlug={slug} />
      <main style={{ flex: 1 }}>
        {slug === 'longevity' && <LongevityLayout {...layoutProps} />}
        {slug === 'nutrition' && <NutritionLayout {...layoutProps} />}
        {slug === 'research' && <ResearchLayout {...layoutProps} />}
        {slug === 'mental-health' && <MentalHealthLayout {...layoutProps} />}
        {slug === 'prevention' && <PreventionLayout {...layoutProps} />}
      </main>
      <SiteFooter />
    </div>
  );
}
